import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import IntroHeroWithImage from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroHeroWithImage";
import SectionArticleOrEventCards from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionArticleOrEventCards";
import SectionHelpAdviceNewsletterPromo from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionHelpAdviceNewsletterPromo";
import { SectionQuickLinksEmbedded } from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionQuickLinks/SectionQuickLinks";
import SectionStandardSignPostCards from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionStandardSignPostCards";
import { usePageManager } from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/uk/components/Layout/Layout";

import { Colours } from "@dogstrust/dt-web-core-lib-front-end/src/utils/colours";
import { getImagePath } from "@dogstrust/dt-web-core-lib-front-end/src/utils/images";
import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
import { graphql } from "gatsby";
import React, { useEffect } from "react";
import * as styles from "./TopicPage.module.scss";

interface Props {
	data: Queries.TopicPageQuery;
}

export const Head: React.FC<Props> = ({ data: { nodeTopic } }) => {
	return (
		<DefaultHead
			pageUrl={nodeTopic.path.alias}
			hrefLangUrl={nodeTopic.field_hreflang_url}
		>
			<SEO
				{...getSeoProps(nodeTopic.field_meta_tags, {
					title: `${nodeTopic.title} | Dogs Trust`,
					description: nodeTopic.field_short_description,
					canonical_url: process.env.GATSBY_DOMAIN_NAME + nodeTopic.path.alias,
					og_type: "article",
					og_url: process.env.GATSBY_DOMAIN_NAME + nodeTopic.path.alias,
					og_title: `${nodeTopic.title} | Dogs Trust`,
					og_description: nodeTopic.field_short_description,
					og_image_url: `${getImagePath(
						"400x300",
						nodeTopic.relationships.field_featured_image?.field_media_image
							?.uri,
					)}`,
					twitter_cards_type: "summary_large_image",
					twitter_cards_description: nodeTopic.field_short_description,
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: `${nodeTopic.title} | Dogs Trust`,
					twitter_cards_page_url:
						process.env.GATSBY_DOMAIN_NAME + nodeTopic.path.alias,
					twitter_cards_image: `${getImagePath(
						"400x300",
						nodeTopic.relationships.field_featured_image?.field_media_image
							?.uri,
					)}`,
				})}
			/>
		</DefaultHead>
	);
};

const TopicPage: React.FC<Props> = ({
	data: {
		nodeTopic,
		helpAdviceTopics: { nodes: otherTopics },
		newsletterPromo,
	},
}) => {
	const { setContentWidth, deregisterPage, usePagination } = usePageManager();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		usePagination(false);
		setContentWidth("DEFAULT");
		return () => deregisterPage();
	}, []);

	return (
		<Layout
			breadcrumbs={nodeTopic?.breadcrumbs ? [...nodeTopic.breadcrumbs] : []}
		>
			<IntroHeroWithImage
				title={nodeTopic.title}
				body={nodeTopic?.field_intro_text?.value}
				imgSrc={
					nodeTopic.relationships.field_featured_image.field_media_image.uri
				}
				imgAlt={
					nodeTopic.relationships.field_featured_image.field_media_image.alt
				}
				borderColour={
					nodeTopic.subTopics.length > 0 ? Colours.SANDY_FUR : Colours.WHITE
				}
			/>
			{nodeTopic.subTopics.length > 0 && (
				<SectionQuickLinksEmbedded
					heading={"Explore by topic"}
					theme={"alternative"}
					minItems={2}
					maxItems={10}
					items={nodeTopic.subTopics.map((topic) => ({
						title: topic.title,

						imgSrc: topic.relationships?.field_icon?.field_media_image?.uri,
						imgAlt: topic.relationships?.field_icon?.field_media_image?.alt,
						url: topic.path?.alias,
						body: topic.field_short_description,
					}))}
				/>
			)}
			<div className={styles.sectionwrapper}>
				<SectionArticleOrEventCards
					title="Suggested articles"
					items={nodeTopic.relationships.field_suggested_articles.map(
						(article) => ({
							title: article.title,
							imgSrc:
								article.relationships?.field_featured_image?.field_media_image
									?.uri,
							imgAlt:
								article.relationships?.field_featured_image?.field_media_image
									?.alt,
							buttonUrl: article.path?.alias,
							buttonLabel: article.field_short_link_text,
							body: article.field_short_description,
							label: article.relationships.field_category_type?.name,
						}),
					)}
					displayOption="accordion"
				/>
				<SectionArticleOrEventCards
					title="All from this topic"
					items={nodeTopic.allTopicArticles.map((article) => ({
						title: article.title,
						imgSrc:
							article.relationships?.field_featured_image?.field_media_image
								?.uri,
						imgAlt:
							article.relationships?.field_featured_image?.field_media_image
								?.alt,
						buttonUrl: article.path?.alias,
						buttonLabel: article.field_short_link_text,
						body: article.field_short_description,
						label: article.relationships.field_category_type?.name,
					}))}
					displayOption="loader"
				/>
				{nodeTopic.siblingTopics.length > 0 ? (
					<SectionStandardSignPostCards
						title="Explore by topic"
						items={nodeTopic.siblingTopics
							.map((topic) => ({
								title: topic.title,
								imgSrc:
									topic.relationships?.field_featured_image?.field_media_image
										?.uri,
								imgAlt:
									topic.relationships?.field_featured_image?.field_media_image
										?.alt,
								buttonUrl: topic.path?.alias,
								buttonLabel: topic.field_short_link_text,
								body: topic.field_short_description,
							}))
							.filter((topic) => topic.title !== nodeTopic.title)}
						withPageBreak={
							nodeTopic.relationships.field_suggested_articles.length > 0
						}
					/>
				) : (
					<SectionStandardSignPostCards
						title="Explore by topic"
						items={otherTopics.map((topic) => ({
							title: topic.title,
							imgSrc:
								topic.relationships?.field_featured_image?.field_media_image
									?.uri,
							imgAlt:
								topic.relationships?.field_featured_image?.field_media_image
									?.alt,
							buttonUrl: topic.path?.alias,
							buttonLabel: topic.field_short_link_text,
							body: topic.field_short_description,
						}))}
						withPageBreak={
							nodeTopic.relationships.field_suggested_articles.length > 0
						}
					/>
				)}
			</div>
			<SectionHelpAdviceNewsletterPromo
				title={newsletterPromo.field_front_end_title}
				body={newsletterPromo.field_section_short_description.value}
				buttonText={newsletterPromo.field_link.title}
				buttonUrl={newsletterPromo.field_link.uri}
			/>
		</Layout>
	);
};
export default TopicPage;

export const query = graphql`
	
    query TopicPage($path: String) {
        nodeTopic(path: { alias: { eq: $path } }) {
            ...Topic
        }
        helpAdviceTopics: allNodeTopic(
            filter: {
                relationships: { field_parent: { name: { eq: "Dog advice" } } }
                path: { alias: { ne: $path } }
            }
        ) {
            nodes {
                ...Topic
            }
        }
        newsletterPromo: nodeSection(
            title: { eq: "Section-Newsletter-Promo" }
        ) {
            field_front_end_title
            field_link {
                title
                uri
            }
            field_section_short_description {
                value
            }
        }
    }
`;
export const fragment = graphql`
    fragment Topic on node__topic {
        __typename
        title
        path {
            alias
        }
        breadcrumbs {
            ...BreadCrumb
        }
		field_hreflang_url
        field_short_description
        field_short_link_text
        field_intro_text {
            value
        }
        field_priority_weight
        field_meta_tags {
            ...MetaTags
        }
        relationships {
            field_icon {
                field_media_image {
                    alt
                    title
                    width
                    height
                    uri
                }
            }
            field_featured_image {
                field_media_image {
                    alt
                    title
                    width
                    height
                    uri
                }
            }
            field_parent {
                name
            }
            field_own_topic {
                name
            }
            field_suggested_articles {
                ...ArticleSignPostData
            }
        }
        allTopicArticles {
            ...ArticleSignPostData
        }
        subTopics {
            path {
                alias
            }
            title
            field_short_description
            field_short_link_text
            relationships {
                field_featured_image {
                    field_media_image {
                        alt
                        title
                        width
                        height
                        uri
                    }
                }
                field_icon {
                    field_media_image {
                        alt
                        title
                        width
                        height
                        uri
                    }
                }
                field_parent {
                    name
                }
                field_own_topic {
                    name
                }
                field_suggested_articles {
                    ...ArticleSignPostData
                }
            }
        }
        siblingTopics {
            path {
                alias
            }
            title
            field_short_description
            field_short_link_text
            relationships {
                field_featured_image {
                    field_media_image {
                        alt
                        title
                        width
                        height
                        uri
                    }
                }
                field_icon {
                    field_media_image {
                        alt
                        title
                        width
                        height
                        uri
                    }
                }
                field_parent {
                    name
                }
                field_own_topic {
                    name
                }
                field_suggested_articles {
                    ...ArticleSignPostData
                }
            }
        }
    }
`;
